import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useClockTextBackgroundData } from './hooks';

import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';

export type TClockTextBackgroundSection =
  Normalize<Queries.SolutionDesktopClockTextBackgroundData_PrismicSolutionDataDesktopSolutionClockTextBackground_Fragment>;

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    position: relative;
    row-gap: 6.25rem;
    justify-content: center;
    justify-items: center;
    background-color: ${colors.backgroundLightDark};
    padding: 5rem 0 6.25rem;
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;

    & > h2 {
      text-align: center;
      line-height: 1.25;
      letter-spacing: -0.115rem;
      color: ${colors.white};
      font-size: 9.375rem;

      ${tp.useFont(fonts => fonts.Switzer.thin)};

      strong {
        line-height: 1;
        font-size: 11.75rem;

        ${tp.useFont(fonts => fonts.Switzer.light)};
      }
    }
  `}
`;

const Clocks = styled.div`
  display: grid;
`;

const ClockItem = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    border-bottom: 3px solid ${colors.white};
  `}
`;

const ClockImage = styled(GatsbyImage)`
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;
  margin-left: 2rem;
  width: 25rem;
  aspect-ratio: 1 / 1;
`;

const ClockLabel = styled.h5`
  ${({ theme: { colors, tp } }) => css`
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
    align-self: flex-end;
    margin: 0 30rem 2rem;
    text-align: center;
    line-height: 0.85;
    letter-spacing: -0.115rem;
    color: ${colors.white}e6;
    font-size: 17.5rem;

    ${tp.useFont(fonts => fonts.Switzer.regular)};
  `}
`;

const ClockTextBackgroundSection: React.FC<TClockTextBackgroundSection> = sliceData => {
  const { clocks, title } = useClockTextBackgroundData(sliceData);

  return (
    <Container>
      <Title render={title} />
      {clocks.length ? (
        <Clocks>
          {clocks.map(({ image, alt, label }, index) => (
            <ClockItem key={`${label}${index}`}>
              <ClockImage image={image} alt={alt} />
              <ClockLabel>{label}</ClockLabel>
            </ClockItem>
          ))}
        </Clocks>
      ) : null}
    </Container>
  );
};

export const desktopClockTextBackgroundFragment = graphql`
  fragment SolutionDesktopClockTextBackgroundData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionClockTextBackground {
      slice_type

      primary {
        solution_clock_text_background_title {
          richText
        }
      }

      items {
        solution_clock_text_background_clock_image {
          gatsbyImageData
          alt
        }
        solution_clock_text_background_clock_label
      }
    }
  }
`;

export default ClockTextBackgroundSection;
